import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { notification } from 'antd'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'

import { authApi, changePasswordSchema } from '@/api/auth.api'
import { currentUserAtom, sessionAtom } from '@/store/auth.atom'
import { useLogout } from '@/hooks/use-logout'
import { AuthLayout } from './AuthLayout'
import { PasswordField } from './PasswordField'

export function ChangePasswordPage() {
  const user = useAtomValue(currentUserAtom)
  const navigate = useNavigate()
  const setSession = useSetAtom(sessionAtom)
  const logout = useLogout()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})

  const validateFieldRealtime = (
    field: 'newPassword' | 'confirmPassword',
    val: string,
  ) => {
    const formData = {
      email: user?.email || '',
      currentPassword,
      newPassword: field === 'newPassword' ? val : newPassword,
      confirmPassword: field === 'confirmPassword' ? val : confirmPassword,
    }

    const result = changePasswordSchema.safeParse(formData)

    if (!result.success) {
      const fieldIssue = result.error.issues.find((i) => i.path[0] === field)
      const confirmIssue = result.error.issues.find(
        (i) => i.path[0] === 'confirmPassword',
      )

      setErrors((prev) => ({
        ...prev,
        [field]: fieldIssue?.message,
        ...(field === 'newPassword' && formData.confirmPassword
          ? { confirmPassword: confirmIssue?.message }
          : {}),
      }))
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
        ...(field === 'newPassword' ? { confirmPassword: undefined } : {}),
      }))
    }
  }

  const handleFieldChange =
    (
      field: 'currentPassword' | 'newPassword' | 'confirmPassword',
      setter: React.Dispatch<React.SetStateAction<string>>,
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setter(val)

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }

      if (field === 'newPassword' || field === 'confirmPassword') {
        validateFieldRealtime(field, val)
      }
    }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const validationResult = changePasswordSchema.safeParse({
      email: user?.email || '',
      currentPassword,
      newPassword,
      confirmPassword,
    })

    if (!validationResult.success) {
      const fieldErrors: Partial<Record<string, string>> = {}
      for (const issue of validationResult.error.issues) {
        const field = String(issue.path[0])
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message
        }
      }
      setErrors(fieldErrors)
      return
    }

    setIsLoading(true)
    try {
      const res = await authApi.changePassword(validationResult.data)
      notification.success({
        title: res?.responseMessage || 'Đổi mật khẩu thành công!',
      })
      await logout.mutateAsync()
    } catch (err: any) {
      setErrors({
        currentPassword: err.message || 'Mật khẩu không hợp lệ',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoBack = () => {
    try {
      setSession(null)
      navigate({ to: '/login', replace: true })
    } catch {}
  }

  return (
    <AuthLayout title="Tạo mật khẩu">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-3.5 sm:gap-4">
          <PasswordField
            label="Mật khẩu hiện tại"
            required
            value={currentPassword}
            onChange={handleFieldChange('currentPassword', setCurrentPassword)}
            placeholder="••••••••"
            autoComplete="off"
            error={errors.currentPassword}
          />

          <PasswordField
            label="Mật khẩu mới"
            required
            value={newPassword}
            onChange={handleFieldChange('newPassword', setNewPassword)}
            placeholder="••••••••"
            autoComplete="new-password"
            error={errors.newPassword}
          />

          <PasswordField
            label="Xác nhận lại mật khẩu"
            required
            value={confirmPassword}
            onChange={handleFieldChange('confirmPassword', setConfirmPassword)}
            placeholder="••••••••"
            autoComplete="new-password"
            error={errors.confirmPassword}
          />

          <div className="mt-1 sm:mt-2 flex gap-2.5 sm:gap-3">
            <Button
              onClick={handleGoBack}
              variant="outline"
              size="lg"
              className="flex-1 text-[14px]"
              disabled={isLoading}
            >
              Quay lại
            </Button>
            <Button
              htmlType="submit"
              variant="primary"
              size="lg"
              className="flex-1 text-[14px]"
              loading={isLoading}
            >
              Xác nhận
            </Button>
          </div>
        </div>
      </form>
    </AuthLayout>
  )
}
